import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  BookOpen,
  Users,
  Calendar,
  Copy,
  Shield,
  ShieldOff
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { lessonService } from '../../services/lessonService';
import { Lesson } from '../../types';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getGrades, getSubjects } from '../../utils/lessons';

interface AdminLessonManagerProps {}

export const AdminLessonManager: React.FC<AdminLessonManagerProps> = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'admin' | 'teacher'>('all');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    if (!user?.isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      return;
    }
    loadLessons();
  }, [user]);

  const loadLessons = async () => {
    try {
      setLoading(true);
      // Load all lessons for admin view
      const allLessons = await lessonService.getAllLessons();
      setLessons(allLessons);
    } catch (error) {
      console.error('Error loading lessons:', error);
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!window.confirm(t('admin.confirmDeleteLesson'))) {
      return;
    }

    try {
      await lessonService.deleteLesson(lessonId);
      setLessons(prev => prev.filter(lesson => lesson.metadata.lesson_id !== lessonId));
      toast.success(t('admin.lessonDeleted'));
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error(t('admin.deleteError'));
    }
  };

  const handleDuplicateLesson = async (lessonId: string) => {
    try {
      const duplicatedLesson = await lessonService.duplicateLesson(lessonId, user?.id);
      setLessons(prev => [duplicatedLesson, ...prev]);
      toast.success(t('admin.lessonDuplicated') || 'Lesson duplicated successfully');
    } catch (error) {
      console.error('Error duplicating lesson:', error);
      toast.error(t('admin.duplicateError') || 'Failed to duplicate lesson');
    }
  };

  const handleToggleAdminStatus = async (lessonId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await lessonService.updateLessonAdminStatus(lessonId, newStatus);
      setLessons(prev => prev.map(lesson => 
        lesson.metadata.lesson_id === lessonId
          ? {
              ...lesson,
              metadata: {
                ...lesson.metadata,
                isAdminLesson: newStatus,
                updated_at: new Date().toISOString()
              }
            }
          : lesson
      ));
      toast.success(
        newStatus 
          ? (t('admin.lessonMarkedAsAdmin') || 'Lesson marked as admin')
          : (t('admin.lessonUnmarkedAsAdmin') || 'Lesson unmarked as admin')
      );
    } catch (error) {
      console.error('Error updating lesson admin status:', error);
      toast.error(t('admin.updateAdminStatusError') || 'Failed to update admin status');
    }
  };

  const subjects = getSubjects(t);
  const grades = getGrades(t);

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = lesson.metadata.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.metadata.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lesson.className && lesson.className.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'admin' && lesson.metadata.isAdminLesson) ||
                         (filterType === 'teacher' && !lesson.metadata.isAdminLesson);
    
    const matchesSubject = selectedSubject === 'All' || lesson.metadata.subject === selectedSubject;
    const matchesGrade = selectedGrade === 'All' || lesson.metadata.grade.toString() === selectedGrade;
    
    return matchesSearch && matchesFilter && matchesSubject && matchesGrade;
  });

  const sortedLessons = [...filteredLessons].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.metadata.created_at).getTime() - new Date(a.metadata.created_at).getTime();
      case 'oldest':
        return new Date(a.metadata.created_at).getTime() - new Date(b.metadata.created_at).getTime();
      case 'title':
        return a.metadata.title.localeCompare(b.metadata.title);
      case 'duration':
        return a.metadata.duration_minutes - b.metadata.duration_minutes;
      case 'subject':
        return a.metadata.subject.localeCompare(b.metadata.subject);
      default:
        return 0;
    }
  });

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('admin.accessDenied')}</h1>
          <p className="text-gray-600">{t('admin.adminPrivilegesRequired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.lessonManagement')}</h1>
          <p className="text-gray-600 mt-1">{t('admin.manageAllLessons')}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Link
            to="/lessons/new"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('admin.createAdminLesson')}</span>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('admin.totalLessons')}</p>
              <p className="text-2xl font-bold text-gray-900">{lessons.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('admin.adminLessons')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {lessons.filter(l => l.metadata.isAdminLesson).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('admin.teacherLessons')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {lessons.filter(l => !l.metadata.isAdminLesson).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col gap-4">
          {/* First row: Search and Type filter */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 min-w-0">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('admin.searchLessons')}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'admin' | 'teacher')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 whitespace-nowrap"
              >
                <option value="all">{t('admin.allLessons')}</option>
                <option value="admin">{t('admin.adminLessons')}</option>
                <option value="teacher">{t('admin.teacherLessons')}</option>
              </select>
            </div>
            
            <div className="text-sm text-gray-600 whitespace-nowrap">
              {t('admin.showingResults', { count: sortedLessons.length, total: lessons.length })}
            </div>
          </div>

          {/* Second row: Subject, Grade, Language, Sort */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 sm:flex-none sm:min-w-[150px]"
            >
              <option value="All">{t('subjects.all')}</option>
              {subjects.map(subject => (
                <option key={subject.value} value={subject.value}>{subject.label}</option>
              ))}
            </select>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 sm:flex-none sm:w-24"
            >
              <option value="All">{t('lessonList.all')}</option>
              {grades.map(grade => (
                <option key={grade.value} value={grade.value}>{grade.label}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 sm:flex-none sm:w-40"
            >
              <option value="recent">{t('lessonList.mostRecent')}</option>
              <option value="oldest">{t('admin.oldest') || 'Oldest first'}</option>
              <option value="title">{t('lessonList.titleAZ')}</option>
              <option value="duration">{t('lessonList.duration')}</option>
              <option value="subject">{t('admin.bySubject') || 'By subject'}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('admin.loadingLessons')}</p>
          </div>
        ) : sortedLessons.length === 0 ? (
          <div className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('admin.noLessonsFound')}</h3>
            <p className="text-gray-600">{t('admin.noLessonsMatchFilters')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sortedLessons.map((lesson) => (
              <div key={lesson.metadata.lesson_id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900 truncate">
                        {lesson.metadata.title}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lesson.metadata.isAdminLesson 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {lesson.metadata.isAdminLesson ? t('admin.adminLesson') : t('admin.teacherLesson')}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1">
                      {lesson.metadata.subject} - {t('lesson.grade')} {lesson.metadata.grade}
                      {lesson.metadata.subconstruct && ` • ${lesson.metadata.subconstruct}`}
                      {lesson.className && ` • ${lesson.className}`}
                    </p>
                    
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>{lesson.metadata.duration_minutes} {t('lesson.minutes')}</span>
                      <span>{lesson.metadata.language.toUpperCase()}</span>
                      <span>{new Date(lesson.metadata.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/session/${lesson.metadata.lesson_id}`}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('admin.viewLesson')}
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <Link
                      to={`/lessons/${lesson.metadata.lesson_id}/edit`}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('admin.editLesson')}
                    >
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleToggleAdminStatus(lesson.metadata.lesson_id, lesson.metadata.isAdminLesson || false)}
                      className={`p-2 rounded-lg transition-colors ${
                        lesson.metadata.isAdminLesson
                          ? 'text-green-600 hover:bg-green-100'
                          : 'text-purple-600 hover:bg-purple-100'
                      }`}
                      title={
                        lesson.metadata.isAdminLesson
                          ? (t('admin.unmarkAsAdmin') || 'Unmark as admin')
                          : (t('admin.markAsAdmin') || 'Mark as admin')
                      }
                    >
                      {lesson.metadata.isAdminLesson ? (
                        <Shield className="w-4 h-4" />
                      ) : (
                        <ShieldOff className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDuplicateLesson(lesson.metadata.lesson_id)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title={t('admin.duplicateLesson') || 'Duplicate lesson'}
                      disabled={lesson.metadata.isAdminLesson !== true}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteLesson(lesson.metadata.lesson_id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title={t('admin.deleteLesson')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
